import * as CL from '@design-system/component-library';
import { useState } from 'react';
import { useLoaderData } from 'react-router';
import { dsClass } from '../../common/constants/dsClasses.js';
import { fetchVaihtoetuManufacturers, fetchVaihtoetuModels } from '../../common/fetch.js';
import { brandMsg, t, technicalErrorMsg } from '../../common/i18n/index.js';
import { VaihtoetuGrading } from './VaihtoetuGrading.js';
import type { CLComboboxItems, CLDropdownItem } from '../../common/types/componentLibrary';
import type { VaihtoetuItemGroupList1 } from '../../generated/api/vaihtoetuItemGroupList1.js';
import type { VaihtoetuManufacturerListData } from '../../generated/api/vaihtoetuManufacturerListData.js';
import type { VaihtoetuModelListData } from '../../generated/api/vaihtoetuModelListData.js';

import './Vaihtoetu.scss';

const mapOptions = (item: VaihtoetuItemGroupList1 | VaihtoetuManufacturerListData): CLDropdownItem => {
  return {
    value: item.Id == null ? undefined : String(item.Id),
    label: item.Name,
  };
};

export const VaihtoetuCalculator = () => {
  const [selectedItemGroup, setSelectedItemGroup] = useState<string | undefined>(undefined);
  const [manufacturers, setManufacturers] = useState<Array<CLDropdownItem>>([]);
  const [selectedManufacturer, setSelectedManufacturer] = useState<string | undefined>(undefined);
  const [models, setModels] = useState<Array<CLDropdownItem>>([]);
  const [selectedModel, setSelectedModel] = useState<number>(0);
  const [technicalError, setTechnicalError] = useState<boolean>(false);

  const itemGroupItems = (useLoaderData() as VaihtoetuItemGroupList1[]).map(mapOptions);

  const onItemGroupSelect = async (groupId: string | undefined) => {
    setSelectedItemGroup(groupId);
    setSelectedManufacturer(undefined);
    setSelectedModel(0);
    if (!groupId) {
      setManufacturers([]);
      return;
    }
    try {
      const response = await fetchVaihtoetuManufacturers(Number(groupId));
      const mappedItems = response.Data.map(mapOptions);
      setManufacturers(mappedItems);
      setTechnicalError(false);
    } catch {
      setTechnicalError(true);
    }
  };

  const onManufacturerSelect = async (manufacturerId: string | undefined) => {
    setSelectedManufacturer(manufacturerId);
    setSelectedModel(0);
    if (!selectedItemGroup || !manufacturerId) {
      setModels([]);
      return;
    }
    try {
      const response = await fetchVaihtoetuModels(Number(selectedItemGroup), Number(manufacturerId));
      const mappedItems = response.Data.map((item: VaihtoetuModelListData) => ({
        value: String(item.Id),
        label: item.ProductName,
      }));
      setModels(mappedItems);
      setTechnicalError(false);
    } catch {
      setTechnicalError(true);
    }
  };

  return (
    <div className={`of-vaihtoetu__calculator ${dsClass.MARGIN_TOP_5} ${dsClass.PADDING_HORIZONTAL_3}`}>
      <div className={`of-vaihtoetu ${dsClass.PADDING_TOP_5} ${dsClass.PADDING_BOTTOM_5}`}>
        <h3 className={dsClass.H3_BOOK}>{t.NDAK('Enter your device details to receive a price estimate')}</h3>
        <p>
          {t.BFBA(
            'Fill in the details to receive an initial price estimate for your device. We will contact you within 24 hours on business days and finalize your trade-in benefit.'
          )}
        </p>
        <div className={dsClass.GRID_ROW}>
          <div className={`${dsClass.GRID_COL_XS_4} ${dsClass.GRID_COL_L_4}`}>
            <CL.Dropdown
              items={itemGroupItems}
              label={t.JQDG('Device type')}
              i18n_dropdown_placeholderText={t.QRYV('Choose')}
              aria-label={t.JQDG('Device type')}
              selectedValue={selectedItemGroup}
              disabled={itemGroupItems.length === 0}
              onValueChange={el => {
                onItemGroupSelect(el.attributes.getNamedItem('data-value')?.value);
              }}
            />
          </div>
          <div className={`${dsClass.GRID_COL_XS_4} ${dsClass.GRID_COL_L_4}`}>
            <CL.Dropdown
              items={manufacturers}
              label={t.GV78(brandMsg)}
              i18n_dropdown_placeholderText={t.QRYV('Choose')}
              aria-label={t.GV78(brandMsg)}
              selectedValue={selectedManufacturer}
              disabled={manufacturers.length === 0}
              onValueChange={el => {
                onManufacturerSelect(el.attributes.getNamedItem('data-value')?.value);
              }}
            />
          </div>
          <div className={`${dsClass.GRID_COL_XS_4} ${dsClass.GRID_COL_L_4}`}>
            <CL.Combobox
              label={t.SKCE('Model')}
              items={models as CLComboboxItems}
              disabled={models.length === 0}
              selectedValue={String(selectedModel)}
              onValueSelect={(value, _) => {
                setSelectedModel(Number(value));
              }}
            />
          </div>
        </div>
        {technicalError && (
          <CL.Disclaimer
            disclaimerType="warning"
            className={dsClass.TEXT_M}
            icon={<CL.Icon icon="warning" />}
            text={t.T4OU(technicalErrorMsg)}
            visible
          />
        )}
        <VaihtoetuGrading modelId={selectedModel} />
      </div>
    </div>
  );
};
